import {
  MappedField,
  SortColumn,
} from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type SlotFields = {
  /**
   * Required for commit form
   */
  controlType?: SlotsPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: SlotsPlaceHolderTypeVal;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: SlotsPlaceHolderTypeVal;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: SlotsPlaceHolderTypeVal;
  /**
   * Used as field in forms
   */
  id: SlotsPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: SlotsPlaceHolderTypeVal;

  /**
   * Adds field explicitly in datagrid columns
   * */
  mandatory?: boolean;

  /**
   * Shows if it should be visible by default in forms
   */
  visible?: SlotsPlaceHolderTypeVal;
};

export type SlotsPlaceHolderTypeVal = {
  createDocsForm?: boolean | number | string;
  createForm?: boolean | number | string;
  createSlotForCalendar?: boolean | number | string;
  datagrid?: boolean | number | string;
  nestedDocsTable?: boolean | number | string;
};

export enum EnumSlotsFieldStructure {
  SlotsTable = 0,
  SlotsCreateForm = 1,
  CreateSlotForCalendar = 2,
  NestedHistoryTable = 3,
  SlotShipmentsTable = 4,
  SlotRecurrenceTable = 5,
  SlotMaterialsTable = 6,
  SlotDocumentsTable = 7,
  SlotDocumentsInfosTable = 8,
  SlotDocumentsInfosForm = 9,
}

export type SlotsFilterUIModel = {
  docks: string[];
  shipmentNumbers?: string;
  slotCodes?: string;
  slotDateFrom?: string;
  slotDateTo?: string;
  sorts?: SortColumn[];
  storages: string[];
};

export type SlotsFilterDto = {
  codes?: string[];
  docks: string[];
  shipmentNumbers?: string[];
  slotDateFrom?: string;
  slotDateTo?: string;
  storages: string[];
};

export enum EnumSlotsViews {
  HISTORY = 1,
}

export type PermissionKey<TRights> = {
  [K in keyof TRights]: NonNullable<TRights[K]> extends boolean ? K : never;
}[keyof TRights];

export class SlotsField {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: SlotFields,
    refId: keyof SlotsPlaceHolderTypeVal,
    actualId: keyof SlotsPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumSlotsFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: SlotFields,
    refId: keyof SlotsPlaceHolderTypeVal,
    actualId: keyof SlotsPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && field.isRequired[actualId]) &&
      !field.mandatory //For adding fields not present in default fields
    ) {
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };
  validFunWithoutUserFields = (
    field: SlotFields,
    refId: keyof SlotsPlaceHolderTypeVal,
    actualId: keyof SlotsPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDefaultFields(type?: EnumSlotsFieldStructure): MappedField[] {
    switch (type) {
      case EnumSlotsFieldStructure.SlotsTable:
        return this.getSlotFields();
      case EnumSlotsFieldStructure.SlotsCreateForm:
        return this.getCreateFormList();
      case EnumSlotsFieldStructure.CreateSlotForCalendar:
        return this.getCreateSlotForCalendarList();
      case EnumSlotsFieldStructure.NestedHistoryTable:
        return this.getSlotHistoryFields();
      case EnumSlotsFieldStructure.SlotShipmentsTable:
        return this.getSlotShipmentsFields();
      case EnumSlotsFieldStructure.SlotRecurrenceTable:
        return this.getSlotRecurrenceFields();
      case EnumSlotsFieldStructure.SlotMaterialsTable:
        return this.getSlotMaterialsFields();
      case EnumSlotsFieldStructure.SlotDocumentsTable:
        return this.getSlotDocumentListFields();
      case EnumSlotsFieldStructure.SlotDocumentsInfosTable:
        return this.getSlotDocumentListInfosFields();
      case EnumSlotsFieldStructure.SlotDocumentsInfosForm:
        return this.getCreateSlotDocumentFields();
      default:
        return this.getSlotFields();
    }
  }

  getSlotFields(): MappedField[] {
    return this.slotFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: field.mandatory ? field.mandatory : false,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreateFormList(): MappedField[] {
    return this.slotFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'createForm');
      })
      .map((field: SlotFields): MappedField => {
        return {
          controlType: field.controlType?.createForm
            ? (field.controlType.createForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.createForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          isRequired: !!field.isRequired?.createForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotFields: SlotFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Type' },
      id: { datagrid: 'type' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'string' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Code' },
      id: { datagrid: 'code' },
      isRequired: { createForm: true },
      visible: { datagrid: false },
    },
    {
      controlType: { createForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Date From' },
      id: { datagrid: 'dateFrom' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Date To' },
      id: { datagrid: 'dateTo' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Storage' },
      id: { datagrid: 'storage' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Dock' },
      id: { datagrid: 'dock' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Forwarder' },
      id: { datagrid: 'forwarder' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Accept Date' },
      id: { datagrid: 'acceptDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Accepted By' },
      id: { datagrid: 'acceptBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Clearence Status' },
      id: { datagrid: 'clearenceStatus' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'WH Container Number' },
      id: { datagrid: 'containerNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Confirm Date' },
      id: { datagrid: 'confirmDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Confirm By' },
      id: { datagrid: 'confirmBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Cancel Date' },
      id: { datagrid: 'cancelDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Cancel By' },
      id: { datagrid: 'cancelBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Unload Permitted Date' },
      id: { datagrid: 'unloadPermittedDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Unload Permitted By' },
      id: { datagrid: 'unloadPermittedBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Arrival Date' },
      id: { datagrid: 'arrivalDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Arrival By' },
      id: { datagrid: 'arrivalBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Departure Date' },
      id: { datagrid: 'departureDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Departure By' },
      id: { datagrid: 'departureBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Status' },
      id: { datagrid: 'status' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Basic Status' },
      id: { datagrid: 'basicStatus' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Last Modified Date' },
      id: { datagrid: 'lastModifiedDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Last Modified By' },
      id: { datagrid: 'lastModifiedBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'checkbox' },
      exclude: { createForm: true },
      headerText: { datagrid: 'IsAccepted' },
      id: { datagrid: 'isAccepted' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'checkbox' },
      exclude: { createForm: true },
      headerText: { datagrid: 'IsConfirmed' },
      id: { datagrid: 'isConfirmed' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'checkbox' },
      exclude: { createForm: true },
      headerText: { datagrid: 'IsCanceled' },
      id: { datagrid: 'isCanceled' },
      mandatory: true,
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Plant' },
      id: { datagrid: 'plant' },
      visible: { datagrid: true },
    },
  ];

  getCreateSlotForCalendarList(): MappedField[] {
    return this.slotCalendarFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'createSlotForCalendar', 'createSlotForCalendar');
      })
      .map((field: SlotFields): MappedField => {
        return {
          controlType: field.controlType?.createSlotForCalendar
            ? (field.controlType.createSlotForCalendar as string)
            : '',
          field: (field.id?.createSlotForCalendar as string) ?? '',
          headerText: (field.headerText?.createSlotForCalendar as string) ?? '',
          isRequired: !!field.isRequired?.createSlotForCalendar,
          visible: !!field.visible?.createSlotForCalendar,
        };
      });
  }

  public readonly slotCalendarFields: SlotFields[] = [
    {
      controlType: { createSlotForCalendar: 'dateTime' },
      headerText: { createSlotForCalendar: 'Date From' },
      id: { createSlotForCalendar: 'dateFrom' },
      isRequired: { createSlotForCalendar: true },
      visible: { createSlotForCalendar: true },
    },
    {
      controlType: { createSlotForCalendar: 'dateTime' },
      headerText: { createSlotForCalendar: 'Date To' },
      id: { createSlotForCalendar: 'dateTo' },
      isRequired: { createSlotForCalendar: true },
      visible: { createSlotForCalendar: true },
    },
    {
      controlType: { createSlotForCalendar: 'dropdown' },
      headerText: { createSlotForCalendar: 'Dock' },
      id: { createSlotForCalendar: 'dock' },
      isRequired: { createSlotForCalendar: true },
      visible: { createSlotForCalendar: true },
    },
    {
      controlType: { createSlotForCalendar: 'dropdown' },
      headerText: { createSlotForCalendar: 'Type' },
      id: { createSlotForCalendar: 'type' },
      isRequired: { createSlotForCalendar: true },
      visible: { createSlotForCalendar: true },
    },
    {
      controlType: { createSlotForCalendar: 'dropdown' },
      headerText: { createSlotForCalendar: 'Forwarder' },
      id: { createSlotForCalendar: 'forwarder' },
      isRequired: { createSlotForCalendar: true },
      visible: { createSlotForCalendar: true },
    },
  ];

  getSlotHistoryFields(): MappedField[] {
    return this.slotHistoryField
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotHistoryField: SlotFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Code' },
      id: { datagrid: 'code' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Type' },
      id: { datagrid: 'type' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Text' },
      id: { datagrid: 'text' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Created Date' },
      id: { datagrid: 'dateCreated' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Created By' },
      id: { datagrid: 'createdBy' },
      visible: { datagrid: true },
    },
  ];

  getSlotShipmentsFields(): MappedField[] {
    return this.slotShipmentsFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: field.mandatory ? field.mandatory : false,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotShipmentsFields: SlotFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Shipment Number' },
      id: { datagrid: 'key' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Track Number' },
      id: { datagrid: 'trackNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Container Number' },
      id: { datagrid: 'containerNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'icon' },
      headerText: { datagrid: 'Commit' },
      id: { datagrid: 'hasDelivery' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'icon' },
      headerText: { datagrid: 'Info' },
      id: { datagrid: 'hasInfo' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Instruction' },
      id: { datagrid: 'instructionType' },
      visible: { datagrid: true },
    },
  ];

  getSlotRecurrenceFields(): MappedField[] {
    return this.slotRecurrenceFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: field.mandatory ? field.mandatory : false,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotRecurrenceFields: SlotFields[] = [
    {
      dataType: { datagrid: 'icon' },
      headerText: { datagrid: 'Without Conflict' },
      id: { datagrid: 'hasConflict' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'START' },
      id: { datagrid: 'dateFrom' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'END' },
      id: { datagrid: 'dateTo' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Code' },
      id: { datagrid: 'code' },
      visible: { datagrid: true },
    },
  ];

  getSlotMaterialsFields(): MappedField[] {
    return this.slotMaterialsFields
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: field.mandatory ? field.mandatory : false,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotMaterialsFields: SlotFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Material' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Quantity' },
      id: { datagrid: 'quantity' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Track Number' },
      id: { datagrid: 'trackNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Invoice Number' },
      id: { datagrid: 'invoiceNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Container Number' },
      id: { datagrid: 'containerNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Forwarder' },
      id: { datagrid: 'forwarder' },
      visible: { datagrid: true },
    },
  ];

  getSlotDocumentListFields(): MappedField[] {
    return this.slotDocumentListField
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getSlotDocumentListInfosFields(): MappedField[] {
    return this.slotDocumentListField
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedDocsTable');
      })
      .map((field: SlotFields): MappedField => {
        return {
          field: (field.id?.nestedDocsTable as string) ?? '',
          headerText: (field.headerText?.nestedDocsTable as string) ?? '',
          isRequired: !!field.isRequired?.nestedDocsTable,
          type: field.dataType?.nestedDocsTable
            ? (field.dataType.nestedDocsTable as string)
            : 'string',
          visible: !!field.visible?.nestedDocsTable,
        };
      });
  }

  getCreateSlotDocumentFields(): MappedField[] {
    return this.slotDocumentListField
      .filter((field: SlotFields): boolean => {
        return this.hasField(field, 'nestedDocsTable', 'createDocsForm');
      })
      .map((field: SlotFields): MappedField => {
        return {
          controlType: field.controlType?.createDocsForm
            ? (field.controlType.createDocsForm as string)
            : '',
          field: (field.id?.nestedDocsTable as string) ?? '',
          headerText:
            (field.headerText?.createDocsForm as string) ??
            (field.headerText?.nestedDocsTable as string) ??
            '',
          isRequired: !!field.isRequired?.createDocsForm,
          visible: !!field.visible?.nestedDocsTable,
        };
      });
  }

  public readonly slotDocumentListField: SlotFields[] = [
    {
      dataType: { datagrid: 'actions', nestedDocsTable: 'actions' },
      headerText: { datagrid: 'Actions', nestedDocsTable: 'Actions' },
      id: { datagrid: 'actions', nestedDocsTable: 'actions' },
      isRequired: { datagrid: true, nestedDocsTable: true },
      visible: { datagrid: true, nestedDocsTable: true },
    },
    {
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Shipment', nestedDocsTable: 'Shipment' },
      id: { datagrid: 'key', nestedDocsTable: 'key' },
      visible: { datagrid: true, nestedDocsTable: false },
    },
    {
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Document Types', nestedDocsTable: 'Document Types' },
      id: { datagrid: 'documentTypes', nestedDocsTable: 'documentTypes' },
      visible: { datagrid: true, nestedDocsTable: false },
    },
    {
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Track Number', nestedDocsTable: 'Track Number' },
      id: { datagrid: 'trackNumber', nestedDocsTable: 'trackNumber' },
      visible: { datagrid: true, nestedDocsTable: false },
    },
    {
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Container Number', nestedDocsTable: 'Container Number' },
      id: { datagrid: 'containerNumber', nestedDocsTable: 'containerNumber' },
      visible: { datagrid: true, nestedDocsTable: false },
    },
    {
      controlType: { createDocsForm: 'file' },
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Name', nestedDocsTable: 'Name' },
      id: { datagrid: 'name', nestedDocsTable: 'name' },
      visible: { datagrid: false, nestedDocsTable: true },
    },

    {
      controlType: { createDocsForm: 'dropdown' },
      dataType: { datagrid: 'string', nestedDocsTable: 'string' },
      headerText: { datagrid: 'Types', nestedDocsTable: 'Types' },
      id: { datagrid: 'types', nestedDocsTable: 'types' },
      isRequired: { createDocsForm: true },
      visible: { datagrid: false, nestedDocsTable: true },
    },
  ];
}
